import { useEffect, useRef } from 'react';

import { logException } from '@paradigm/logging/src/logging';
import {
  useTradeTapeIdsList,
  useTradeTapeService,
} from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';

import {
  useAgentActions,
  useAgentState,
} from '#/features/terminal/agent-context';

const DEBOUNCE_MS = 500;
const MAX_TRADES = 50;

/**
 * Pumps the visible UnifiedRfqs trade-tape into the AI agent as a structured
 * `set_block_trade_context` gateway request. The agent's general LLM brain
 * then has the live tape in its session context and can answer questions
 * about block-trade flow without any per-tool MCP plumbing.
 *
 * Update strategy:
 *   - Debounce changes by `DEBOUNCE_MS` so a burst of trades doesn't spam
 *     the gateway.
 *   - Cap at the latest `MAX_TRADES` rows to keep the LLM context tight.
 *   - On unmount, clear the context so the agent stops referring to a stale
 *     view when the user closes the tab.
 *
 * This component renders nothing.
 */
export default function BlockTradeContextPump() {
  const agentState = useAgentState();
  const agentActions = useAgentActions();
  const tradeIds = useTradeTapeIdsList();
  const service = useTradeTapeService();

  const isAuthenticated = agentState.status === 'authenticated';
  const tradeIdsRef = useRef(tradeIds);
  tradeIdsRef.current = tradeIds;

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const handle = setTimeout(() => {
      const latestIds = tradeIdsRef.current.slice(0, MAX_TRADES);
      const trades = latestIds
        .map((id) => service.selectById(id))
        .filter((t): t is NonNullable<typeof t> => t != null);

      agentActions
        .request('set_block_trade_context', {
          source: 'paradigm.unified_rfqs.trade_tape',
          ts: Date.now(),
          visible_count: tradeIdsRef.current.length,
          trades,
        })
        .catch((err: unknown) => {
          // Gateways that don't implement `set_block_trade_context` will
          // reject the request — that's fine, it just means the agent has
          // no live tape context. Log and move on.
          logException('terminal:set_block_trade_context', err);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [tradeIds, service, isAuthenticated, agentActions]);

  // Clear context on unmount (closing the tab / disconnecting).
  useEffect(() => {
    return () => {
      agentActions.request('clear_block_trade_context', {}).catch(() => {
        // Best-effort cleanup; ignore failures.
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
